import React, { useEffect, useState, useMemo } from "react";
import Cookies from "js-cookie";
import {
  AccseptTask,
  ComplateTask,
  fetchTaskMamnerById,
} from "../../Redux/Slice/TaskMasterSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SupportStyleTable from "../../Components/Table/SupportStyleTable";
import "../CustomerReq/InhouseRnd.css";
import {
  Calendar,
  Clock,
  CheckCircle2,
  CheckCheck,
  Eye,
  Building2,
  User,
  Star,
} from "lucide-react";

const MainTask = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("queue");
  const [currentTime, setCurrentTime] = useState(new Date());

  const loginData = Cookies.get("loginData");
  const parsedData = useMemo(() => {
    return loginData ? JSON.parse(loginData) : null;
  }, [loginData]);

  const { TaskMamnerData } = useSelector((state) => state.TaskMaster);

  // Keep live time clock ticking
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchTaskList = () => {
    if (parsedData?.data?._id) {
      dispatch(fetchTaskMamnerById(parsedData.data._id));
    }
  };

  useEffect(() => {
    fetchTaskList();
  }, [dispatch, parsedData?.data?._id]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "--";
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "--";
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const handleShowTask = (id) => {
    navigate(`/TaskDetailsDetail/${id}`);
  };

  const handleAcceptTask = async (taskId) => {
    try {
      const resultAction = await dispatch(AccseptTask(taskId));
      if (AccseptTask.fulfilled.match(resultAction)) {
        toast.success("Task accepted successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        fetchTaskList();
      } else {
        toast.error(resultAction.payload || "Failed to accept task", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error("An error occurred while accepting task", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleCompleteTask = async (id) => {
    try {
      const resultAction = await dispatch(ComplateTask(id));
      if (ComplateTask.fulfilled.match(resultAction)) {
        toast.success("Task completed successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        fetchTaskList();
      } else {
        toast.error(resultAction.payload || "Failed to complete task", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error("An error occurred while completing task", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Reusable cell renderers
  const renderIdCell = (params) => (
    <span className="inline-flex items-center justify-center font-bold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-[11px]">
      #{params.value}
    </span>
  );

  const renderCompanyCell = (params) => (
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center text-[#133A69] shrink-0 font-bold text-[11px]">
        <Building2 size={13} />
      </div>
      <span className="font-semibold text-slate-800 truncate" title={params.value}>
        {params.value || "--"}
      </span>
    </div>
  );

  const renderPersonCell = (params) => (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 text-slate-600 flex items-center justify-center font-bold text-[10px] shrink-0">
        <User size={12} />
      </div>
      <span className="font-medium text-slate-700 truncate" title={params.value}>
        {params.value || "--"}
      </span>
    </div>
  );

  const renderDateCell = (params) => (
    <div className="inline-flex items-center gap-1.5 text-slate-600 font-medium text-xs">
      <Calendar size={13} className="text-slate-400 shrink-0" />
      <span>{params.value || "--"}</span>
    </div>
  );

  const renderDueDateCell = (params) => (
    <div className="inline-flex items-center gap-1.5 font-semibold text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60">
      <Clock size={12} className="text-amber-500 shrink-0" />
      <span>{params.value || "--"}</span>
    </div>
  );

  const renderRatingCell = (params) => (
    <span className="inline-flex items-center gap-1 font-bold text-xs px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <Star size={11} className="fill-emerald-500 text-emerald-500" />
      <span>{params.value || "Not Rated"}</span>
    </span>
  );

  // Column definitions
  const PendingColumns = [
    { field: "id", headerName: "ID", width: 65, minWidth: 60, renderCell: renderIdCell },
    { field: "CompanyId", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 160, renderCell: renderPersonCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 130, renderCell: renderDateCell },
    { field: "DueDate", headerName: "DUE DATE", minWidth: 130, renderCell: renderDueDateCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title="View Full Task Details"
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "acceptTask",
      headerName: "ACCEPT TASK",
      width: 125,
      minWidth: 125,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-accept shadow-xs"
          onClick={() => handleAcceptTask(params.row.MainId)}
          title="Accept Task"
        >
          <CheckCircle2 size={13} />
          <span>Accept Task</span>
        </button>
      ),
    },
  ];

  const AcceptColumns = [
    { field: "id", headerName: "ID", width: 65, minWidth: 60, renderCell: renderIdCell },
    { field: "CompanyId", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 160, renderCell: renderPersonCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 130, renderCell: renderDateCell },
    { field: "DueDate", headerName: "DUE DATE", minWidth: 130, renderCell: renderDueDateCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title="View Full Task Details"
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "completeTask",
      headerName: "COMPLETE TASK",
      width: 135,
      minWidth: 135,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-complete shadow-xs"
          onClick={() => handleCompleteTask(params.row.MainId)}
          title="Complete Task"
        >
          <CheckCheck size={14} />
          <span>Complete Task</span>
        </button>
      ),
    },
  ];

  const CompleteColumns = [
    { field: "id", headerName: "ID", width: 65, minWidth: 60, renderCell: renderIdCell },
    { field: "CompanyId", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 160, renderCell: renderPersonCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 130, renderCell: renderDateCell },
    { field: "DueDate", headerName: "DUE DATE", minWidth: 130, renderCell: renderDueDateCell },
    { field: "Rating", headerName: "RATING", minWidth: 120, renderCell: renderRatingCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title="View Full Task Details"
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  // Rows mapping
  const PendingRows = useMemo(() => {
    return (
      TaskMamnerData?.Panding?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        AssignedBy: el?.AssignedBy?.Name || "--",
        CompanyId: el?.CompanyId?.CompanyName || el?.CompanyId || "--",
        DueDate: formatDate(el.DueDate),
        AssignDate: formatDate(el.AssignDate),
      })) || []
    );
  }, [TaskMamnerData]);

  const AcceptRows = useMemo(() => {
    return (
      TaskMamnerData?.Accept?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        AssignedBy: el?.AssignedBy?.Name || "--",
        CompanyId: el?.CompanyId?.CompanyName || el?.CompanyId || "--",
        DueDate: formatDate(el.DueDate),
        AssignDate: formatDate(el.AssignDate),
      })) || []
    );
  }, [TaskMamnerData]);

  const CompleteRows = useMemo(() => {
    return (
      TaskMamnerData?.Complate?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        AssignedBy: el?.AssignedBy?.Name || "--",
        CompanyId: el?.CompanyId?.CompanyName || el?.CompanyId || "--",
        DueDate: formatDate(el.DueDate),
        AssignDate: formatDate(el.AssignDate),
        Rating: el.Rating || "Not Rated",
      })) || []
    );
  }, [TaskMamnerData]);

  return (
    <div className="dashboard-page-container">
      {/* Compact Header Banner with Title and Live Clock */}
      <div className="page-header-banner-compact">
        <div>
          <h4>Main Task Dashboard</h4>
          <span className="page-subtitle">
            Manage your assigned main tasks, in-queue tasks, and completions
          </span>
        </div>

        {/* Live Date & Time Clock */}
        <div className="dashboard-live-clock">
          <span className="clock-date">
            <Calendar size={13} className="text-slate-500" />
            {currentTime.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
          <span className="clock-divider"></span>
          <span className="clock-time">
            <Clock size={13} className="text-[#133A69]" />
            {currentTime.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })}
          </span>
          <span className="live-dot" title="Live clock"></span>
        </div>
      </div>

      {/* Status-Themed Tab Buttons */}
      <div className="dashboard-tabs-bar">
        <button
          type="button"
          className={`tab-btn-status tab-btn-queue ${activeTab === "queue" ? "active" : ""}`}
          onClick={() => setActiveTab("queue")}
        >
          <span className="tab-status-dot"></span>
          <span>In Queue Task</span>
          <span className="tab-badge">{PendingRows.length}</span>
        </button>

        <button
          type="button"
          className={`tab-btn-status tab-btn-running ${activeTab === "running" ? "active" : ""}`}
          onClick={() => setActiveTab("running")}
        >
          <span className="tab-status-dot"></span>
          <span>Running Task</span>
          <span className="tab-badge">{AcceptRows.length}</span>
        </button>

        <button
          type="button"
          className={`tab-btn-status tab-btn-complete ${activeTab === "complete" ? "active" : ""}`}
          onClick={() => setActiveTab("complete")}
        >
          <span className="tab-status-dot"></span>
          <span>Complete Task</span>
          <span className="tab-badge">{CompleteRows.length}</span>
        </button>
      </div>

      {/* Main Table Area Filling Remaining Screen Height */}
      <div className="w-full flex-grow flex flex-col min-h-0 overflow-hidden">
        {/* Tab 1: In Queue Task */}
        {activeTab === "queue" && (
          <div className="inhouse-card h-full">
            <div
              className="inhouse-card-header"
              style={{ borderLeft: "4px solid #f59e0b" }}
            >
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-amber-600">
                  In Queue Task
                </span>
                <span className="text-slate-500 text-xs font-medium">
                  Assigned main tasks awaiting acceptance
                </span>
              </div>
              <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                Total: {PendingRows.length}
              </span>
            </div>
            <div className="inhouse-card-body">
              <SupportStyleTable
                tableId="main_task_queue"
                fileName="Main_Tasks_InQueue"
                columns={PendingColumns}
                rows={PendingRows}
                fullHeight={true}
                emptyComponent="No in-queue tasks found."
              />
            </div>
          </div>
        )}

        {/* Tab 2: Running Task */}
        {activeTab === "running" && (
          <div className="inhouse-card h-full">
            <div
              className="inhouse-card-header"
              style={{ borderLeft: "4px solid #1d4ed8" }}
            >
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-blue-600">
                  Running Task
                </span>
                <span className="text-slate-500 text-xs font-medium">
                  Currently active and accepted in-progress tasks
                </span>
              </div>
              <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                Total: {AcceptRows.length}
              </span>
            </div>
            <div className="inhouse-card-body">
              <SupportStyleTable
                tableId="main_task_running"
                fileName="Main_Tasks_Running"
                columns={AcceptColumns}
                rows={AcceptRows}
                fullHeight={true}
                emptyComponent="No running tasks found."
              />
            </div>
          </div>
        )}

        {/* Tab 3: Complete Task */}
        {activeTab === "complete" && (
          <div className="inhouse-card h-full">
            <div
              className="inhouse-card-header"
              style={{ borderLeft: "4px solid #10b981" }}
            >
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-emerald-600">
                  Complete Task
                </span>
                <span className="text-slate-500 text-xs font-medium">
                  Finished and delivered main tasks
                </span>
              </div>
              <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                Total: {CompleteRows.length}
              </span>
            </div>
            <div className="inhouse-card-body">
              <SupportStyleTable
                tableId="main_task_complete"
                fileName="Main_Tasks_Completed"
                columns={CompleteColumns}
                rows={CompleteRows}
                fullHeight={true}
                emptyComponent="No completed tasks found."
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainTask;
